import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom";
import crel from "crelt";
import type { Node } from "prosemirror-model";
import { NodeSelection } from "prosemirror-state";
import type { EditorView, NodeView } from "prosemirror-view";

import { isImageNode } from "../adapter/schema";
import { createPencilEditSvg } from "../icons";
import { key as pageStateKey } from "../plugins/page-state";

import { t } from "i18n/i18n";

type HandleCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

// Matches --page-default-max-width. Kept as a constant rather than reading the CSS variable
// at runtime to avoid getComputedStyle overhead on every pointermove during drag.
const MAX_IMAGE_WIDTH_PX = 660;

function clampWidth(raw: number): number {
  return Math.round(Math.min(MAX_IMAGE_WIDTH_PX, Math.max(20, raw)));
}

const POSITION_STYLES: Record<HandleCorner, string> = {
  "top-left": "-top-1.5 -left-1.5",
  "top-right": "-top-1.5 -right-1.5",
  "bottom-right": "-bottom-1.5 -right-1.5",
  "bottom-left": "-bottom-1.5 -left-1.5",
};

const HANDLE_CONFIGS: { corner: HandleCorner; cursor: string }[] = [
  { corner: "top-left", cursor: "nwse-resize" },
  { corner: "top-right", cursor: "nesw-resize" },
  { corner: "bottom-right", cursor: "nwse-resize" },
  { corner: "bottom-left", cursor: "nesw-resize" },
];

/**
 * ProseMirror NodeView for images. This serves two functions:
 * providing resize handlers for authors, and providing an alt text editing panel for authors.
 *
 * Resize UX behavior:
 * - Left-side handles (top-left, bottom-left): drag right shrinks the image
 * - Right-side handles (top-right, bottom-right): drag right enlarges the image
 * - Direct DOM mutation during drag; single AttrStep dispatched on pointerup
 *
 * Uses pointer events + setPointerCapture so drag works on touch/stylus devices
 * and events are routed to the handle without document-level listeners.
 * Uses selectNode/deselectNode to show/hide handles, and ignoreMutation to
 * prevent ProseMirror from treating live style changes as content edits.
 *
 * Alt text editing panel behavior:
 * - Trigger button appears when the image is selected and the page is in authoring mode
 * - Clicking the trigger opens a popover panel with a textarea and a "mark as decorative" checkbox
 * - Submitting the form dispatches an AttrStep to update the node's alt text and isDecorative attributes
 * - Canceling or closing the panel reverts any changes made in the form
 * - The panel is positioned below the image using floating-ui's computePosition
 * - The panel is removed from the DOM when the image is deselected or the page exits authoring mode
 */
export class ImageView implements NodeView {
  dom: HTMLSpanElement;

  private img: HTMLImageElement;

  private resizeHandles: HTMLDivElement;

  private altTextTrigger: HTMLButtonElement;

  private altTextTriggerCleanup: (() => void) | null = null;

  private altTextPanel: HTMLDivElement;

  private altTextPanelCleanup: (() => void) | null = null;

  private view: EditorView;

  private getPos: () => number | undefined;

  // Drag state
  private isDragging = false;

  private dragIsLeftSide = false;

  private dragStartX = 0;

  private dragStartWidth = 0;

  constructor(node: Node, view: EditorView, getPos: () => number | undefined) {
    this.view = view;
    this.getPos = getPos;

    this.dom = document.createElement("span");
    this.dom.className = "image-wrapper group";

    this.img = document.createElement("img");
    this.img.className = "inline-block align-bottom";

    // stopEvent returns true for click on this.img when in authoring mode, so
    // ProseMirror won't handle it there. We dispatch a NodeSelection ourselves
    // to ensure the image is selected and the resize handles are shown, even when
    // clicking from an unfocused state.
    this.img.addEventListener("click", () => {
      if (!this.isAuthoringPage) return;
      const pos = this.getPos();
      if (pos !== undefined) {
        this.view.dispatch(
          this.view.state.tr.setSelection(NodeSelection.create(this.view.state.doc, pos)),
        );
      }
    });

    this.resizeHandles = this.createResizeHandles();

    this.dom.appendChild(this.img);
    this.dom.appendChild(this.resizeHandles);

    this.altTextTrigger = this.createAltTextTrigger();
    if (this.isAuthoringPage) {
      this.attachAltTextTrigger();
    }

    this.applyNodeAttrs(node);
  }

  get rootId(): string | undefined {
    return pageStateKey.getState(this.view.state)?.rootId;
  }

  get isAuthoringPage(): boolean {
    return pageStateKey.getState(this.view.state)?.isAuthoring ?? false;
  }

  private applyNodeAttrs(node: Node): void {
    if (!isImageNode(node)) return;
    const { src, width, alt, isDecorative } = node.attrs;
    this.img.src = src;
    if (typeof alt === "string") {
      if (isDecorative) {
        this.img.dataset.alt = alt;
        this.img.alt = "";
      } else {
        this.img.alt = alt;
        delete this.img.dataset.alt;
      }
    } else if (isDecorative) {
      delete this.img.dataset.alt;
    } else {
      this.img.alt = "";
    }
    if (typeof isDecorative === "boolean") {
      if (isDecorative) {
        this.img.dataset.decorative = "true";
      } else {
        delete this.img.dataset.decorative;
      }
    }
    if (width) {
      this.img.style.width = `${width}px`;
    } else {
      this.img.style.width = "";
    }
  }

  private createResizeHandles(): HTMLDivElement {
    const resizeHandles = document.createElement("div");
    resizeHandles.className = "image-resize-handles";

    for (const handleConfig of HANDLE_CONFIGS) {
      const handle = document.createElement("div");
      handle.className = `pointer-events-auto absolute box-border size-3 touch-none border border-[#777] bg-white opacity-80 ${POSITION_STYLES[handleConfig.corner]}`;
      handle.dataset.corner = handleConfig.corner;
      handle.style.cursor = handleConfig.cursor;

      handle.addEventListener("pointerdown", (e: PointerEvent) => {
        e.preventDefault();
        e.stopPropagation();
        handle.setPointerCapture(e.pointerId);
        this.startResize(e, handleConfig.corner.includes("left"));
      });

      handle.addEventListener("pointermove", (e: PointerEvent) => {
        if (!this.isDragging) return;
        const deltaX = e.clientX - this.dragStartX;
        const raw = this.dragIsLeftSide
          ? this.dragStartWidth - deltaX // left handle: drag right = shrink
          : this.dragStartWidth + deltaX; // right handle: drag right = grow
        this.img.style.width = `${clampWidth(raw)}px`;
      });

      handle.addEventListener("pointerup", (e: PointerEvent) => {
        if (!this.isDragging) return;
        this.stopResize();

        const deltaX = e.clientX - this.dragStartX;
        const newWidth = clampWidth(
          this.dragIsLeftSide ? this.dragStartWidth - deltaX : this.dragStartWidth + deltaX,
        );

        const pos = this.getPos();
        if (pos !== undefined) {
          const tr = this.view.state.tr.setNodeAttribute(pos, "width", newWidth);
          this.view.dispatch(tr);
        }
      });

      // No-op after normal pointerup (isDragging already false). On abnormal cancellation,
      // reverts the DOM width so it stays consistent with what other users see.
      handle.addEventListener("lostpointercapture", () => {
        if (!this.isDragging) return;
        this.stopResize();
        this.img.style.width = `${this.dragStartWidth}px`;
      });

      resizeHandles.appendChild(handle);
    }

    return resizeHandles;
  }

  private createAltTextPanel(): HTMLDivElement {
    const isDecorative = !!this.img.dataset.decorative;
    const altText = isDecorative ? this.img.dataset.alt || "" : this.img.alt || "";

    const [headingId, descriptionId] = this.rootId
      ? [`${this.rootId}-alt-text-heading`, `${this.rootId}-alt-text-description`]
      : [undefined, undefined];

    const form = document.createElement("form");
    form.className = "flex flex-col gap-4 w-[400px] p-6";
    const heading = crel(
      "h3",
      { class: "text-base text-black-tint-10 font-medium", id: headingId },
      t("asset_field.alt_text"),
    );
    form.appendChild(heading);
    const description = crel(
      "p",
      { class: "text-sm font-normal text-black-tint-55", id: descriptionId },
      t("asset_field.alt_text_description"),
    );
    form.appendChild(description);

    const saveButton = crel(
      "button",
      { type: "submit", class: "popover-button popover-button-submit", disabled: true },
      t("common.save"),
    ) as HTMLButtonElement;

    const textarea = crel("textarea", {
      class:
        "w-full rounded-md border border-black-tint-90 p-2 " +
        "disabled:border-black-tint-97 disabled:bg-black-tint-97 disabled:text-black-tint-70",
      name: "alt",
      value: altText,
      rows: 3,
      placeholder: t("asset_field.alt_text_placeholder"),
      "aria-labelledby": headingId,
      "aria-describedby": descriptionId,
    }) as HTMLTextAreaElement;

    const checkbox = crel("input", {
      type: "checkbox",
      name: "isDecorative",
      checked: isDecorative,
    }) as HTMLInputElement;

    /** Save button is disabled if (and only if) there are no changes in the form:
     * - textarea value is the same as the node's alt attribute
     * - checkbox checked state is the same as the node's isDecorative attribute
     */
    const setSaveButtonDisabledState = () => {
      const currentIsDecorative = !!this.img.dataset.decorative;
      const currentAltText = currentIsDecorative ? this.img.dataset.alt || "" : this.img.alt || "";

      saveButton.disabled =
        textarea.value === currentAltText && checkbox.checked === currentIsDecorative;
    };
    textarea.addEventListener("input", setSaveButtonDisabledState);
    checkbox.addEventListener("change", () => {
      textarea.disabled = checkbox.checked;
      setSaveButtonDisabledState();
    });

    const label = crel("label", { class: "flex items-center gap-2 text-black-tint-20" }, [
      checkbox,
      t("asset_field.mark_as_decorative"),
    ]);

    form.appendChild(textarea);
    form.appendChild(label);

    const cancelButton = crel(
      "button",
      { type: "button", class: "popover-button popover-button-cancel" },
      t("common.cancel"),
    ) as HTMLButtonElement;
    cancelButton.addEventListener("click", () => {
      this.detachAltTextPanel();
      // reset form values
      const currentIsDecorative = !!this.img.dataset.decorative;
      const currentAltText = currentIsDecorative ? this.img.dataset.alt || "" : this.img.alt || "";
      textarea.value = currentAltText;
      checkbox.checked = currentIsDecorative;
      textarea.disabled = currentIsDecorative;
    });
    const buttonContainer = crel("div", { class: "mt-4 flex flex-row-reverse gap-2 self-end" }, [
      saveButton,
      cancelButton,
    ]);
    form.appendChild(buttonContainer);

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const pos = this.getPos();
      if (pos !== undefined) {
        const tr = this.view.state.tr
          .setNodeAttribute(pos, "alt", textarea.value)
          .setNodeAttribute(pos, "isDecorative", checkbox.checked);
        this.view.dispatch(tr);
      }
      this.detachAltTextPanel();
    });

    const panel = crel(
      "div",
      {
        class:
          "absolute z-30 rounded-lg border-none bg-black-tint-99 p-2 text-sm font-normal shadow-modal " +
          "supports-squircle:rounded-[24px] supports-squircle:corner-squircle " +
          "hidden group-focus-within/prosemirror:block",
        "aria-labelledby": headingId,
        "aria-describedby": descriptionId,
        role: "dialog",
      },
      [form],
    ) as HTMLDivElement;

    return panel;
  }

  private createAltTextTrigger(): HTMLButtonElement {
    const triggerIcon = createPencilEditSvg();
    triggerIcon.classList.add("size-3");

    const trigger = crel(
      "button",
      {
        class:
          "absolute z-20 h-[30px] w-fit cursor-pointer items-center justify-center gap-1.5 rounded-md border-black-tint-20 bg-black-tint-20 px-2 py-1 text-xs font-normal text-white whitespace-nowrap " +
          "hover:border-black-tint-30 hover:bg-black-tint-30 active:border-blue active:bg-blue-shade-40 supports-squircle:rounded-[12px] supports-squircle:corner-squircle " +
          "hidden group-focus-within/prosemirror:group-data-[selected]:flex aria-[expanded=true]:invisible",
        type: "button",
      },
      [triggerIcon, t("asset_field.alt_text")],
    ) as HTMLButtonElement;

    this.altTextPanel = this.createAltTextPanel();

    trigger.setAttribute("aria-expanded", "false");
    trigger.addEventListener("click", () => {
      const currentIsDecorative = !!this.img.dataset.decorative;
      const currentAltText = currentIsDecorative ? this.img.dataset.alt || "" : this.img.alt || "";
      const textarea = this.altTextPanel.querySelector("textarea");
      if (textarea) {
        textarea.value = currentAltText;
        textarea.disabled = currentIsDecorative;
      }
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const checkbox = this.altTextPanel.querySelector(
        "input[type=checkbox]",
      ) as HTMLInputElement | null;
      if (checkbox) {
        checkbox.checked = currentIsDecorative;
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      const saveButton = this.altTextPanel.querySelector(
        "button[type=submit]",
      ) as HTMLButtonElement | null;
      if (saveButton && textarea && checkbox) {
        saveButton.disabled =
          textarea.value === currentAltText && checkbox.checked === currentIsDecorative;
      }

      this.attachAltTextPanel({
        focusEl: this.altTextPanel.querySelector("textarea") || undefined,
      });
    });

    return trigger;
  }

  attachAltTextTrigger() {
    if (this.altTextTrigger.parentElement !== this.dom) {
      this.dom.appendChild(this.altTextTrigger);
    }
    if (this.altTextTriggerCleanup) {
      this.altTextTriggerCleanup();
    }
    this.altTextTriggerCleanup = autoUpdate(this.dom, this.altTextTrigger, () => {
      computePosition(this.dom, this.altTextTrigger, {
        placement: "bottom",
        middleware: [
          offset(3),
          flip({
            crossAxis: false,
            padding: {
              top: 40,
            },
          }),
          shift({
            boundary: this.dom.closest("main") || undefined,
            padding: {
              top: 3,
              bottom: 3,
              left: 3,
              right: 3,
            },
          }),
        ],
      }).then(({ x, y }) => {
        Object.assign(this.altTextTrigger.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
      });
    });
  }

  detachAltTextTrigger() {
    this.altTextTrigger.remove();
    if (this.altTextTriggerCleanup) {
      this.altTextTriggerCleanup();
      this.altTextTriggerCleanup = null;
    }
  }

  attachAltTextPanel({ focusEl }: { focusEl?: HTMLElement } = {}) {
    if (this.altTextPanel.parentElement !== this.dom) {
      this.dom.appendChild(this.altTextPanel);
    }
    this.altTextTrigger.setAttribute("aria-expanded", "true");
    this.altTextTrigger.ariaControlsElements = [this.altTextPanel];

    if (this.altTextPanelCleanup) {
      this.altTextPanelCleanup();
    }

    let isFirstUpdate = true;
    this.altTextPanelCleanup = autoUpdate(this.dom, this.altTextPanel, () => {
      computePosition(this.dom, this.altTextPanel, {
        placement: "bottom",
        middleware: [
          offset(3),
          flip({
            crossAxis: false,
            padding: {
              top: 40,
            },
          }),
          shift({
            boundary: this.dom.closest("main") || undefined,
            padding: {
              top: 3,
              bottom: 3,
              left: 3,
              right: 3,
            },
          }),
        ],
      }).then(({ x, y }) => {
        Object.assign(this.altTextPanel.style, {
          left: `${x}px`,
          top: `${y}px`,
        });
        if (isFirstUpdate && focusEl) {
          focusEl.focus();
        }
        isFirstUpdate = false;
      });
    });
  }

  detachAltTextPanel() {
    this.altTextPanel.remove();
    this.altTextTrigger.setAttribute("aria-expanded", "false");
    this.altTextTrigger.ariaControlsElements = [];
    if (this.altTextPanelCleanup) {
      this.altTextPanelCleanup();
      this.altTextPanelCleanup = null;
    }
  }

  update(node: Node): boolean {
    if (!isImageNode(node)) return false; // let ProseMirror handle the node view
    this.applyNodeAttrs(node);

    if (this.isAuthoringPage) {
      this.attachAltTextTrigger();
    } else {
      this.detachAltTextTrigger();
      this.detachAltTextPanel();
    }
    return true; // update applied; keep this node view
  }

  selectNode(): void {
    this.dom.classList.add("is-selected");
    this.dom.dataset.selected = "true";
  }

  deselectNode(): void {
    this.dom.classList.remove("is-selected");
    delete this.dom.dataset.selected;

    this.detachAltTextPanel();
  }

  stopEvent(event: Event): boolean {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return false;

    // Let the alt text trigger/panel handle all events within itself.
    if (this.altTextPanel.contains(target)) return true;
    if (this.altTextTrigger.contains(target)) return true;

    // Prevent ProseMirror from handling pointer/mouse events on handles so that
    // the node stays selected and the drag can begin cleanly.
    if (["pointerdown", "mousedown"].includes(event.type)) {
      return "corner" in target.dataset;
    }

    // Prevent ProseMirror's click handler from resetting the NodeSelection after
    // mousedown already established it. Without this, clicking the image from an
    // unfocused editor can cause the resize handles to not appear.
    if (event.type === "click" && target === this.img && this.isAuthoringPage) {
      return true;
    }

    return false; // let ProseMirror handle all other events
  }

  // eslint-disable-next-line class-methods-use-this
  ignoreMutation(): boolean {
    // Prevent ProseMirror from treating live style.width changes during drag,
    // or resize handle visibility changes, as document content mutations.
    return true;
  }

  private startResize(e: PointerEvent, isLeftSide: boolean): void {
    this.isDragging = true;
    this.dragIsLeftSide = isLeftSide;
    this.dragStartX = e.clientX;
    this.dragStartWidth = this.img.offsetWidth;

    // Lock cursor globally so it doesn't flicker as the pointer moves over other elements
    document.body.style.cursor = (e.target as HTMLElement).style.cursor;
  }

  private stopResize(): void {
    this.isDragging = false;
    document.body.style.cursor = "";
  }

  destroy(): void {
    // Reset state in case the view is destroyed mid-drag
    this.stopResize();
    // Remove alt text trigger/panel and clean up floating-ui listeners
    this.detachAltTextTrigger();
    this.detachAltTextPanel();
  }
}

export function imageNodeView(node: Node, view: EditorView, getPos: () => number | undefined) {
  return new ImageView(node, view, getPos);
}
